import { ChangeEvent, ForwardedRef, forwardRef, useImperativeHandle, useRef } from "react";
import RadioButton, { RadioButtonProps } from "../RadioButton";
import Textbox from "../Textbox";
import styles from "./styles.module.scss";

export type RadioButtonWithTextboxChangeEvent = ChangeEvent<HTMLInputElement> & {
  target: EventTarget & HTMLInputElement & {
    checked: boolean;
    value: string | number | undefined;
    text: string;
  };
}

export type RadioButtonWithTextboxProps = Omit<RadioButtonProps, "onChange"> & {
  onChange?: (event: RadioButtonWithTextboxChangeEvent) => void,
  text?: string,
};

export type RadioButtonWithTextboxRef = ForwardedRef<{
  radio: HTMLInputElement,
  textbox: HTMLInputElement,
}>;

const RadioButtonWithTextbox = ({ className, checked, children, disabled, placeholder, onChange, text, ...props }: RadioButtonWithTextboxProps, ref: RadioButtonWithTextboxRef) => {
  const radio = useRef<HTMLInputElement>(null);
  const textbox = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    radio: radio.current as HTMLInputElement,
    textbox: textbox.current as HTMLInputElement,
  }), []);

  return (
    <RadioButton
      className={`${styles.radioButtonWithTextbox} ${className ?? ""}`}
      ref={radio}
      checked={checked}
      disabled={disabled}
      onChange={(e) => {
        if (textbox.current !== null) {
          if (e.target.checked) {
            textbox.current.focus();
          }
          if (onChange !== undefined) {
            const newTarget = Object.assign(e.target, {
              text: textbox.current.value,
            });
            const newEvent = Object.assign({}, e, {
              target: newTarget,
            });
            onChange(newEvent);
          }
        }
      }}
      {...props}
    >
      <div
        className={styles.content}
      >
        {children}
        <div className={styles.inputWrapper}>
          <Textbox
            ref={textbox}
            className={`${styles.input} ${disabled ? styles.disabled : undefined}`}
            type="text"
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => {
              if (radio.current !== null && onChange !== undefined) {
                const newTarget = Object.assign(radio.current, {
                  checked: true,
                  value: props.value,
                  text: e.target.value,
                });
                const newEvent = Object.assign({}, e, {
                  target: newTarget,
                });
                onChange(newEvent);
              }
            }}
            value={text}
          />
        </div>
      </div>
    </RadioButton>
  );
};

export default forwardRef(RadioButtonWithTextbox);